/**
 * Kontakt vlastníka / osoby u akviziční smlouvy (souhlas, digitální cesta, …):
 * e-mail nebo telefon. Stačí u **jedné** osoby celkově (ne u každé).
 * Parita web / Android / iOS / sign API — přítomnost kontaktu, ne přísné E.164.
 */

/** Odstraní mezery, pomlčky, závorky; ponechá číslice (a případné vedoucí + jen přes digits). */
export function digitsOnlyPhone(raw: string | null | undefined): string {
  return String(raw ?? '').replace(/\D/g, '');
}

/**
 * Národní 9místné CZ mobilní číslo (začíná 6/7), pokud vstup vypadá jako CZ mobil.
 * Přijímá např. `777551165`, `777 551 165`, `+420777551165`, `00420 777 551 165`.
 */
export function extractCzMobileNationalDigits(
  raw: string | null | undefined,
): string | null {
  let digits = digitsOnlyPhone(raw);
  if (!digits) return null;
  if (digits.startsWith('00420') && digits.length >= 14) {
    digits = digits.slice(5);
  } else if (digits.startsWith('420') && digits.length >= 12) {
    digits = digits.slice(3);
  }
  if (digits.length === 9 && /^[67]\d{8}$/.test(digits)) {
    return digits;
  }
  return null;
}

/**
 * Rozumný CZ mobil (9 číslic 6/7… s volitelným +420 / 00420 a oddělovači).
 * Pro sign gate stačí i libovolný neprázdný telefon — viz {@link hasOwnerContact}.
 */
export function isAcceptableCzMobilePhone(
  raw: string | null | undefined,
): boolean {
  return extractCzMobileNationalDigits(raw) != null;
}

/** Neprázdný e-mail nebo telefon (trim) — podmínka před digitálním podpisem ACQ smlouvy. */
export function hasOwnerContact(
  email?: string | null,
  phone?: string | null,
): boolean {
  if ((email ?? '').trim()) return true;
  const phoneTrim = (phone ?? '').trim();
  if (!phoneTrim) return false;
  // Explicitně uznávaný CZ mobil i po normalizaci oddělovačů.
  if (isAcceptableCzMobilePhone(phoneTrim)) return true;
  // Backend / legacy: jakýkoli neprázdný telefon (pevná linka, zahraničí, …).
  return true;
}

/** První neprázdný e-mail / telefon z řádků vlastníků (draft / snapshot / list). */
export function firstOwnerContactFromRows(
  sources: Array<
    Array<{ email?: string | null; telefon?: string | null }> | null | undefined
  >,
): { email: string; phone: string } {
  let email = '';
  let phone = '';
  for (const owners of sources) {
    for (const o of owners ?? []) {
      if (!email) email = (o.email ?? '').trim();
      if (!phone) phone = (o.telefon ?? '').trim();
      if (email && phone) return { email, phone };
    }
  }
  return { email, phone };
}

export function hasOwnerContactFromRows(
  sources: Array<
    Array<{ email?: string | null; telefon?: string | null }> | null | undefined
  >,
  legacyEmail?: string | null,
  legacyPhone?: string | null,
): boolean {
  const fromOwners = firstOwnerContactFromRows(sources);
  if (hasOwnerContact(fromOwners.email, fromOwners.phone)) return true;
  return hasOwnerContact(legacyEmail, legacyPhone);
}

/**
 * Unikátní neprázdné e-maily z řádků vlastníků (+ volitelný legacy sloupec).
 * Pro odeslání finálního PDF po podpisu (souhlas / digitální cesta).
 */
export function collectOwnerEmailsFromRows(
  sources: Array<
    Array<{ email?: string | null; telefon?: string | null }> | null | undefined
  >,
  legacyEmail?: string | null,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (raw: string | null | undefined) => {
    const e = (raw ?? '').trim();
    if (!e) return;
    const key = e.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(e);
  };
  for (const owners of sources) {
    for (const o of owners ?? []) {
      push(o.email);
    }
  }
  push(legacyEmail);
  return out;
}
